import { isOfflineMobileAccess } from "@/domains/offline/connectivity/deviceEligibility";
import { probeApiReachability } from "@/domains/offline/connectivity/connectivityProbe";

type ConnectivityListener = (online: boolean) => void;

let online = typeof navigator !== "undefined" ? navigator.onLine : true;
let offlineEnabled = isOfflineMobileAccess();
const listeners = new Set<ConnectivityListener>();

let confirmTimer: number | null = null;
let confirmGeneration = 0;

export function getConnectivityOnline(): boolean {
  return offlineEnabled ? online : true;
}

export function getOfflineEnabled(): boolean {
  return offlineEnabled;
}

export function setConnectivityOnline(next: boolean): void {
  if (online === next) {
return;
}
  online = next;
  listeners.forEach((l) => l(getConnectivityOnline()));
}

export function setOfflineEnabled(next: boolean): void {
  if (offlineEnabled === next) {
return;
}
  offlineEnabled = next;
  listeners.forEach((l) => l(getConnectivityOnline()));
}

export function subscribeConnectivity(listener: ConnectivityListener): () => void {
  listeners.add(listener);
  listener(getConnectivityOnline());
  return () => listeners.delete(listener);
}

async function runConnectivityConfirm(): Promise<void> {
  if (!offlineEnabled) {
    return;
  }

  const generation = ++confirmGeneration;

  if (navigator.onLine) {
    setConnectivityOnline(true);
    return;
  }

  const reachable = await probeApiReachability();
  if (generation !== confirmGeneration) {
    return;
  }

  setConnectivityOnline(reachable);
}

export function refreshConnectivityProbe(): void {
  if (!offlineEnabled) {
    return;
  }

  if (confirmTimer != null) {
    window.clearTimeout(confirmTimer);
  }

  confirmTimer = window.setTimeout(() => {
    confirmTimer = null;
    void runConnectivityConfirm();
  }, 250);
}

export function initConnectivityListeners(): () => void {
  const onOnline = () => setConnectivityOnline(true);
  const onOffline = () => refreshConnectivityProbe();
  const onPageShow = () => refreshConnectivityProbe();
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      refreshConnectivityProbe();
    }
  };

  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  window.addEventListener("pageshow", onPageShow);
  document.addEventListener("visibilitychange", onVisibilityChange);

  void runConnectivityConfirm();

  return () => {
    if (confirmTimer != null) {
      window.clearTimeout(confirmTimer);
      confirmTimer = null;
    }

    confirmGeneration += 1;
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    window.removeEventListener("pageshow", onPageShow);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
